import { Link } from '@mm/components';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import {
  PlanBillingPeriodBE,
  PlanTypeBE,
} from '@mm/state/interfaces/subscription';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import {
  activeFacebookPageSelector,
  enhancedBillingPagesSelector,
} from '@mm/state/selectors/facebookPageSelector';
import {
  enhancedPlanInfoSelector,
  enhancedPlansSelector,
} from '@mm/state/selectors/subscriptionSelectors';
import {
  selectActiveCompanyId,
  selectCompanies,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { isArray } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Menu, MenuItem, SubMenu } from '@szhsin/react-menu';
import { Redirect, useHistory, useLocation, useParams } from 'react-router';
import MainSelect from '../../../common/components/MainSelect/MainSelect';
import Spinner from '../../../common/components/Spinner';
import { SettingsRoutes } from '../../../routes/settings-routes';
import { Routes } from '../../../routes/top-level-routes';
import { BillingModalHeader } from '../SettingsPage/modals/BillingModal/BillingModalHeader/BillingModalHeader';
import CheckoutForm from '../SettingsPage/modals/BillingModal/CheckoutForm/CheckoutForm';
import styles from './Subscribe.module.css';
import { CHATBOT_EDITOR, SIGNOUT } from '../../../common/constants/routes';
import { MenuOutlined } from '@ant-design/icons';
import { useSetNextOnboardingStep } from '../SignupOnboarding/use-set-next-onboarding-step';

type SubscribeRouteParams = {
  planCode?: string;
};

type SubscribeRouterState = {
  force: boolean;
};

export const Subscribe = () => {
  const history = useHistory();
  const { state } = useLocation<SubscribeRouterState>();
  const { planCode } = useParams<SubscribeRouteParams>();
  const { retrieveBasicListByCompany, attachPagesTeam } =
    useConnectedPageCRUDActions();
  const { getPlanInfo, getPlans, getAccountData } =
    useSubscriptionCRUDActions();
  const { getById } = useCompanyCRUDActions();
  const usersCRUDActions = useUsersCRUDActions();
  const connectedPageCRUDActions = useConnectedPageCRUDActions();
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [code, setCode] = useState<string | undefined>(planCode);
  const [selectedPage, setSelectedPage] = useState<
    undefined | EnhancedBillingPage
  >(undefined);
  const allPages = useSelector(enhancedBillingPagesSelector);
  const activePage = useSelector(activeFacebookPageSelector);
  const session = useSelector((state: StoreState) => state.session.session);
  const finishedOnboarding =
    session.userAttributes.onboardingSignupStep === OnboardingSignupStep.NONE;
  const plans = useSelector(enhancedPlansSelector);
  const plansMapping = useSelector(
    (state: StoreState) => state.subscription.plansMapping,
  );
  const planInfo = useSelector(enhancedPlanInfoSelector);
  const plan = planInfo || plans.find(({ planCode }) => planCode === code);
  const proPlan =
    plan &&
    [PlanTypeBE.pro, PlanTypeBE.proSpecial].indexOf(plan.planType) !== -1;
  const companyId = useSelector(selectActiveCompanyId);
  const [pagesRetrieved, setPagesRetrieved] = useState(false);
  const pages = allPages.filter(({ companyId: id }) => id === companyId);
  const freePages = pages.filter(p => !p.subscription && !p.redemption);
  const company = useSelector((state: StoreState) =>
    state.company.entities.find(({ id }) => id === companyId),
  );
  const companies = useSelector(selectCompanies);
  const filteredCompanies = companies.filter(c => c.id !== companyId);
  const { setNextOnboardingStep } = useSetNextOnboardingStep();

  const handleCompanyClick = (id: number) => {
    usersCRUDActions.updateActiveCompanyId(session.id, id);
    connectedPageCRUDActions.nullifyConnectedPage();
    setNextOnboardingStep(OnboardingSignupStep.NONE);
    history.push(CHATBOT_EDITOR);
  };

  useEffect(() => {
    if (pagesRetrieved) {
      if (pages.length === 0 && !company?.freePricePlan) {
        history.push(Routes.chatbotEditor.path);
      }
    }
  }, [pagesRetrieved]);
  useEffect(() => {
    if (proPlan && freePages.length) {
      setSelectedPage(
        freePages.find(({ id }) => id === activePage?.id) || freePages[0],
      );
    }
  }, [proPlan, freePages.length]);
  useEffect(() => {
    if (!companyId || code) {
      return;
    }
    (async () => {
      if (!code) {
        if (!plansMapping) {
          await getPlans(companyId);
        } else {
          const plan = isArray(plansMapping.unicorn)
            ? plansMapping.unicorn[0]
            : plansMapping.unicorn;
          setCode(plan);
        }
      }
    })();
  }, [code, plansMapping, companyId]);
  useEffect(() => {
    if (!code) {
      return;
    }
    if (!companyId) {
      return;
    }
    (async () => {
      try {
        setLoading(true);
        if (!company) {
          await getById(companyId);
        }
        await getAccountData(companyId);
        await retrieveBasicListByCompany(companyId, { withSubscription: true });
        setPagesRetrieved(true);
        if (plan?.planCode !== code) {
          await getPlanInfo(code);
        }
      } catch (e) {
        if (e instanceof Response && e.status === 404) {
          history.push(Routes.chatbotEditor.path);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [code, companyId]);

  const skipOnboarding = async () => {
    await usersCRUDActions.updateUserAttributes(companyId, session.id, {
      onboardingSignupStep: OnboardingSignupStep.NONE,
    });
  };

  if (
    activePage?.subscription ||
    activePage?.redemption ||
    (freePages.length === 0 && pages.length > 0)
  ) {
    return <Redirect to={Routes.chatbotEditor.getLink()} />;
  }
  return (
    <div>
      {loading ? (
        <Spinner show={true} className={styles.spinner} />
      ) : (
        <div className={styles.container}>
          <Menu
            menuButton={
              <div className={styles.menu}>
                <MenuOutlined />
              </div>
            }
          >
            {filteredCompanies?.length && (
              <SubMenu label="Switch Company">
                {filteredCompanies.map(company => {
                  return (
                    <MenuItem
                      onClick={() => handleCompanyClick(company.id)}
                      key={company.id}
                    >
                      {company.name}
                    </MenuItem>
                  );
                })}
              </SubMenu>
            )}
            <MenuItem onClick={() => history.push(SIGNOUT)}>Logout</MenuItem>
          </Menu>
          {isFetching && <Spinner show={true} className={styles.spinner} />}
          <div className={styles.checkoutForm}>
            {plan && company && (
              <>
                <BillingModalHeader plan={plan} />
                {state?.force && (
                  <div className={styles.redirectLabel}>
                    OmniChat by MobileMonkey now requires a subscription. See{' '}
                    <a
                      href="https://mobilemonkey.com/prices"
                      target="_blank"
                      className={styles.supportLink}
                      rel="noopener noreferrer"
                    >
                      mobilemonkey.com/prices
                    </a>{' '}
                    for details
                  </div>
                )}
                {proPlan && freePages.length > 0 && (
                  <div className={styles.facebookPageSelectorWrapper}>
                    <label className={styles.checkoutFormLabel}>
                      Facebook Page / Website
                    </label>
                    <MainSelect
                      value={selectedPage?.id}
                      onChange={e => {
                        setSelectedPage(
                          freePages.find(
                            ({ id }) => id === Number(e.target.value),
                          ),
                        );
                      }}
                      className={styles.facebookPageSelect}
                    >
                      {freePages.map(page => (
                        <option key={page.id} value={page.id}>
                          {page.name}
                        </option>
                      ))}
                    </MainSelect>
                  </div>
                )}
                <CheckoutForm
                  facebookPage={selectedPage}
                  plan={plan}
                  activeFacebookPagePlanInfo={{}}
                  setIsFetching={setIsFetching}
                  onPlanCreatedCallback={async subscription => {
                    setIsFetching(false);
                    if (subscription) {
                      if (
                        [PlanTypeBE.pro, PlanTypeBE.proSpecial].indexOf(
                          plan.planType,
                        ) === -1
                      ) {
                        if (freePages.length === 1 && pages.length === 1) {
                          await attachPagesTeam(company.id, subscription.id, [
                            freePages[0].id,
                          ]);
                        }
                      }
                      if (subscription.planType === PlanTypeBE.team) {
                        skipOnboarding();
                        history.push(Routes.thankYou.path);
                      } else {
                        history.push(Routes.signupOnboarding.path);
                      }
                    }
                  }}
                  paywall={true}
                  company={company}
                  manualRedirect={true}
                  isAnnual={plan?.billingPeriod === PlanBillingPeriodBE.year}
                />
                {pages.length > 0 && finishedOnboarding && (
                  <span className={styles.billingSettingsLinkContainer}>
                    Or go to{' '}
                    <Link
                      className={styles.billingSettingsLink}
                      to={SettingsRoutes.billing.path}
                    >
                      Billing Settings
                    </Link>
                  </span>
                )}
                <div className={styles.supportLabel}>
                  Have questions? Send an email to{' '}
                  <a
                    className={styles.supportLink}
                    href="mailto:sales@mobilemonkey.com"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    sales@mobilemonkey.com
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
